'use client'

import { useId, useState } from 'react'

import Button from '/react4xp/components/prototype/Button'
import Form from '/react4xp/components/prototype/Form/Form'
import Input from '/react4xp/components/prototype/Form/Input'
import Textarea from '/react4xp/components/prototype/Form/Textarea'
import Checkbox from '/react4xp/components/prototype/Form/Checkbox'

import type { TwoColumnFormData, TwoColumnFormProps } from './types'

// The consent label is assembled as an HTML string (see Checkbox), so a
// caller-supplied URL has to be escaped before it lands in the href attribute.
function escapeAttr(value: string) {
	return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;')
}

export default function TwoColumnForm({
	headline,
	intro,
	image,
	formTitle,
	privacyLink,
	tooltipLabel,
	labels = {},
	onSubmit,
}: TwoColumnFormProps) {
	const formId = useId()
	const nameId = `${formId}-name`
	const emailId = `${formId}-email`
	const locationId = `${formId}-location`
	const messageId = `${formId}-message`
	const consentId = `${formId}-consent`

	const {
		nameLabel = 'Name',
		emailLabel = 'Email',
		locationLabel = 'Project location',
		messageLabel = 'Message',
		consentPrefix = 'I have read and agree to the',
		consentLinkLabel = 'privacy policy',
		nameRequiredMessage = 'Name is required',
		emailRequiredMessage = 'Email is required',
		invalidEmailMessage = 'Invalid email address',
		messageRequiredMessage = 'Message is required',
		consentRequiredMessage = 'You must accept the privacy policy',
		sendButtonLabel = 'Send',
		successTitle = 'Thank you for your inquiry!',
		successSubtitle,
		resetButtonLabel = 'Send a new inquiry',
		genericErrorMessage = 'Something went wrong. Please try again.',
	} = labels

	const [isSent, setIsSent] = useState(false)
	const [submitError, setSubmitError] = useState('')

	const linkHtml = privacyLink?.url
		? `<a href="${escapeAttr(privacyLink.url)}"${
				privacyLink.isExternal ? ' target="_blank" rel="noopener noreferrer"' : ''
			}>${consentLinkLabel}</a>`
		: consentLinkLabel
	const consentLabel = `${consentPrefix} ${linkHtml}<span aria-hidden="true" class="form-label-required">*</span>`

	async function handleFormSubmit(data: Record<string, unknown>) {
		setSubmitError('')

		const formData: TwoColumnFormData = {
			name: (data.name as string) ?? '',
			email: (data.email as string) ?? '',
			location: data.location as string | undefined,
			message: (data.message as string) ?? '',
			consent: data.consent === true || data.consent === 'true',
		}

		try {
			await onSubmit?.(formData)
			setIsSent(true)
		} catch {
			setSubmitError(genericErrorMessage)
		}
	}

	function handleReset() {
		setIsSent(false)
		setSubmitError('')
	}

	return (
		<section className="bg-brand-50 py-12">
			<div className="container">
				<div className="flex flex-col lg:flex-row gap-16">
					<div className="min-w-0 flex-1 flex flex-col gap-6">
						{headline && <h2 className="h1 text-black-950">{headline}</h2>}
						{intro && <p className="body text-black-950">{intro}</p>}
						{image && (
							<figure className="w-full max-w-full aspect-1400/701 overflow-hidden">
								<img src={image.src} alt={image.alt || ''} className="w-full h-full object-cover" />
							</figure>
						)}
					</div>

					<div className="lg:max-w-175 flex-1">
						{isSent ? (
							<div className="flex flex-col gap-6 h-full justify-center">
								<h2 className="h2 text-black-950">{successTitle}</h2>
								{successSubtitle && <p className="body text-black-950">{successSubtitle}</p>}
								<Button variant="primary" size="sm" type="button" className="self-start" onClick={handleReset}>
									{resetButtonLabel}
								</Button>
							</div>
						) : (
							<>
								{formTitle && <h2 className="h2 text-black-950 mb-6">{formTitle}</h2>}
								<Form onSubmit={handleFormSubmit} className="flex flex-col gap-6 lg:gap-3">
									<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
										<Input
											id={nameId}
											name="name"
											label={nameLabel}
											type="text"
											required
											requiredMessage={nameRequiredMessage}
										/>
										<Input
											id={emailId}
											name="email"
											label={emailLabel}
											type="email"
											required
											requiredMessage={emailRequiredMessage}
											invalidEmailMessage={invalidEmailMessage}
										/>
									</div>

									<Input id={locationId} name="location" label={locationLabel} type="text" />

									<Textarea
										id={messageId}
										name="message"
										label={messageLabel}
										required
										requiredMessage={messageRequiredMessage}
										className="min-h-[7.9375rem]"
									/>

									<Checkbox
										id={consentId}
										name="consent"
										label={consentLabel}
										required
										requiredMessage={consentRequiredMessage}
										tooltipLabel={tooltipLabel}
									/>

									{submitError && (
										<p role="alert" className="text-sm leading-[1.4] text-red">
											{submitError}
										</p>
									)}

									<Button variant="primary" size="sm" type="submit" className="self-start mt-2">
										{sendButtonLabel}
									</Button>
								</Form>
							</>
						)}
					</div>
				</div>
			</div>
		</section>
	)
}
